import { isLeft } from "fp-ts/lib/Either";
import type { ParsingRule } from "../../types";
import { short } from "diagnostics-lib/io";
import * as io from "io-ts";

/**
 * @description Expected csv columns. We can list multiple name for a given column to ensure legacy or changes.
 * Use to check the file content.
 */
export const columnsMandatory = [
  ["name", "technicalName"],
  ["outputModelName"],
  ["offsetExpected"],
];

export const hasInvalidOutputModelNameSize = (outputModelName: string) =>
  outputModelName && isLeft(short(io.string).decode(outputModelName));

/**
 * @description Check if file contains all required columns.
 */
export const hasInvalidColumns: ParsingRule = (header: string[]) =>
  columnsMandatory.every(columnsM => columnsM.some(col => header?.includes(col))) ? false : true;

/**
 * @description Check if file contains all required columns.
 */
export const hasInvalidFileEmpty: ParsingRule = (content: string[]) => content.length === 0;

/**
 * @description Check if file contains duplicated rows (based on name|technicalName column).
 */
export const hasDuplicatedRowByQuantityId: ParsingRule = (quantityIds: (string | undefined)[]) =>
  quantityIds.length > new Set(quantityIds).size;

/**
 * @description Check if the name column, on a given line, contain empty value.
 */
export const hasNoValueForQuantityName: ParsingRule = (quantityId: string) =>
  quantityId.length === 0;

/**
 * @description Check if the name column, on a given line, contain unexpected value.
 */
export const hasUnexpectedQuantityName: ParsingRule = (
  quantityName: string,
  expectedNameValues: string[],
) => (expectedNameValues.includes(quantityName) ? false : true);

/*
 * @description Check if the offsetExpected value, on a given line, is a number.
 */
export const hasInvalidOffsetExpected: ParsingRule = (offsetExpected: string) =>
  offsetExpected && !Number.isFinite(Number(offsetExpected)) ? true : false;

/**
 * @description Check if the offsetExpected column is filled in when the variable is a outputModelName, on a given line.
 */
export const hasInvalidDefinitionOutputModelName: ParsingRule = (
  outputModelName: string,
  offsetExpected: string,
) => (outputModelName && !offsetExpected ? true : false);

/**
 * @description Check if the outputModelName column is filled in when the variable is a offsetExpected, on a given line.
 */
export const hasInvalidDefinitionOffsetExpected: ParsingRule = (
  outputModelName: string,
  offsetExpected: string,
) => (!outputModelName && offsetExpected ? true : false);
